import CopyIcon from "@mui/icons-material/CopyAllRounded";
import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import { Button } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import {
  getRowSelectionModelFromIds,
  getSelectedRowIdsFromModel,
} from "@entrilia/shared/components/grid/gridHelper.ts";
import type { Dashboard } from "../../../api/types/dashboardsTypes.ts";
import useClientContext from "../../../context/useClientContext.ts";
import { getCheckedGridSx } from "../../common/grid/gridStyles";
import { getColumnDefinitions } from "./dashboardsGridDataProvider";
import { pluralize } from "@entrilia/shared/utilities/stringHelper";

interface Props {
  dashboards: Dashboard[];
  isLoading: boolean;
  getDashboardPath: (id: string) => string;
  onDelete: (ids: string[]) => void;
  onDuplicate: (id: string) => void;
  onCopy: (ids: string[]) => void;
}

const DashboardsList = ({ dashboards, isLoading, getDashboardPath, onDelete, onDuplicate, onCopy }: Props) => {
  const { hasAnyPermission } = useClientContext();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const hasAccessToManageDashboards = useMemo(() => hasAnyPermission(["ManageDashboards"]), [hasAnyPermission]);
  const showToolbar = Boolean(selectedIds.length) && hasAccessToManageDashboards;

  const columnDefinitions = useMemo(
    () =>
      getColumnDefinitions({
        hasAccessToManageDashboards,
        getDashboardPath,
        onDelete: (dashboardId) => onDelete([dashboardId]),
        onDuplicate,
        onCopy,
      }),
    [hasAccessToManageDashboards, getDashboardPath, onDelete, onDuplicate, onCopy]
  );

  const onDeleteClick = useCallback(() => {
    onDelete(selectedIds);
    setSelectedIds([]);
  }, [onDelete, selectedIds]);

  const onCopyClick = useCallback(() => {
    onCopy(selectedIds);
  }, [onCopy, selectedIds]);

  return (
    <DataGridHeaderToolbarProvider
      showToolbar={showToolbar}
      selection={[selectedIds, setSelectedIds]}
      actions={
        <>
          <Button variant="text" color="secondary" startIcon={<CopyIcon />} onClick={onCopyClick}>
            Copy {pluralize("Dashboard", selectedIds.length)}
          </Button>
          <Button variant="text" color="error" startIcon={<DeleteIcon />} onClick={onDeleteClick}>
            Delete
          </Button>
        </>
      }
    >
      <DataGrid<Dashboard>
        getRowId={getRowId}
        sx={getCheckedGridSx}
        loading={isLoading}
        noRowsText="No dashboards"
        columns={columnDefinitions}
        rows={dashboards}
        checkboxSelection={hasAccessToManageDashboards}
        slots={{ columnHeaders: DataGridColumnHeadersWithToolbar }}
        rowSelectionModel={getRowSelectionModelFromIds(selectedIds)}
        onRowSelectionModelChange={(newSelection) => setSelectedIds(getSelectedRowIdsFromModel(newSelection))}
        disableColumnSorting
      />
    </DataGridHeaderToolbarProvider>
  );
};

export default DashboardsList;

const getRowId = (row: Dashboard) => row.dashboardId;
